/**
 * Text effect preset definitions and canvas renderers.
 *
 * Simple effects (none, gradient_fill, chrome_stroke) → handled via Konva Text
 * node props; no offscreen canvas needed.
 *
 * Complex effects (extrude_3d, image_through_text, motion_blur) → rendered
 * to an offscreen HTMLCanvasElement at 2× density for sharpness, then loaded
 * as a Konva Image node.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type EffectId =
  | 'none'
  | 'gradient_fill'
  | 'chrome_stroke'
  | 'extrude_3d'
  | 'image_through_text'
  | 'motion_blur';

export interface EffectParams {
  // Gradient Fill
  gradientTopColor: string;
  gradientBottomColor: string;
  // Chrome Stroke
  strokeColor: string;
  strokeWidth: number;
  shadowOffset: number;
  // 3D Extrude
  extrudeDepth: number;
  extrudeColor: string;
  // Motion Blur
  blurAmount: number;
}

export const DEFAULT_EFFECT_PARAMS: EffectParams = {
  gradientTopColor: '#ffd700',
  gradientBottomColor: '#ff4500',
  strokeColor: '#c0c0c0',
  strokeWidth: 3,
  shadowOffset: 3,
  extrudeDepth: 6,
  extrudeColor: '#2a2a2a',
  blurAmount: 12,
};

export interface EffectPreset {
  id: EffectId;
  label: string;
  description: string;
  /** Complex effects require offscreen canvas rendering (not Konva Text) */
  isComplex: boolean;
}

export const EFFECT_PRESETS: EffectPreset[] = [
  { id: 'none',               label: 'None',        description: 'Plain solid text',           isComplex: false },
  { id: 'gradient_fill',      label: 'Gradient',    description: 'Vertical color gradient',    isComplex: false },
  { id: 'chrome_stroke',      label: 'Chrome',      description: 'Metallic stroke & shadow',   isComplex: false },
  { id: 'extrude_3d',         label: '3D Extrude',  description: 'Dimensional depth layers',   isComplex: true  },
  { id: 'image_through_text', label: 'Image Mask',  description: 'Image visible through text', isComplex: true  },
  { id: 'motion_blur',        label: 'Motion Blur', description: 'Horizontal speed blur',      isComplex: true  },
];

// ─── Konva prop overrides for simple effects ──────────────────────────────────

/**
 * Returns extra props to spread onto a Konva `<Text>` node for simple effects.
 * For 'none' this returns an empty object (uses the layer's existing fill).
 */
export function getKonvaTextEffectProps(
  effect: EffectId,
  params: EffectParams,
  fontSize: number,
): Record<string, unknown> {
  if (effect === 'gradient_fill') {
    return {
      fill: undefined,  // fill is overridden by gradient
      fillLinearGradientStartPoint: { x: 0, y: 0 },
      fillLinearGradientEndPoint: { x: 0, y: fontSize * 1.4 },
      fillLinearGradientColorStops: [0, params.gradientTopColor, 1, params.gradientBottomColor],
    };
  }

  if (effect === 'chrome_stroke') {
    return {
      stroke: params.strokeColor,
      strokeWidth: Math.max(1, params.strokeWidth),
      shadowColor: 'rgba(0,0,0,0.7)',
      shadowOffsetX: params.shadowOffset,
      shadowOffsetY: params.shadowOffset,
      shadowBlur: params.shadowOffset * 2,
    };
  }

  return {};
}

// ─── Offscreen canvas rendering for complex effects ───────────────────────────

export interface CanvasRenderResult {
  /** The rendered HTMLCanvasElement (at 2× internal resolution) */
  canvas: HTMLCanvasElement;
  /** Logical (display-px) height of the rendered area */
  displayHeight: number;
  /** Logical (display-px) x-offset — how far the canvas origin is left of the layer x */
  padX: number;
  /** Logical (display-px) y-offset — how far the canvas origin is above the layer y */
  padY: number;
}

export interface CanvasRenderParams {
  text: string;
  fontFamily: string;
  fontSize: number;       // display-space px
  fontStyle: string;      // 'normal' | 'bold' | 'italic' | 'bold italic'
  fill: string;
  align: 'left' | 'center' | 'right';
  displayWidth: number;   // text box width in display-space px
  effect: EffectId;
  effectParams: EffectParams;
  bgImage: HTMLImageElement | null;
  lineHeight?: number;       // multiplier (default 1.3)
  letterSpacing?: number;    // display-space px (default 0)
}

/**
 * Renders a complex text effect to an HTMLCanvasElement.
 * Returns canvas + layout info so the caller can position it correctly.
 */
export function renderEffectCanvas(params: CanvasRenderParams): CanvasRenderResult {
  const {
    text, fontFamily, fontSize, fontStyle, fill, align,
    displayWidth, effect, effectParams, bgImage,
    lineHeight: lineHeightRatio = 1.3,
    letterSpacing: lsDisplay = 0,
  } = params;

  const PR = 2; // internal pixel ratio for sharpness

  // ── Measure & wrap ──────────────────────────────────────────────────────────
  const measureCanvas = document.createElement('canvas');
  const mCtx = measureCanvas.getContext('2d')!;
  mCtx.font = buildFont(fontStyle, fontSize * PR, fontFamily);
  // Apply letter spacing to measurement context if supported
  if (lsDisplay !== 0 && 'letterSpacing' in mCtx) {
    (mCtx as any).letterSpacing = `${lsDisplay * PR}px`;
  }

  const wrappedLines = wrapText(mCtx, text, displayWidth * PR);
  const numLines = wrappedLines.length;
  const lineH = fontSize * PR * lineHeightRatio;
  const textH = numLines * lineH;

  // ── Canvas padding (to accommodate overflow from effects) ──────────────────
  const rawPad = effect === 'extrude_3d'
    ? Math.max(4, effectParams.extrudeDepth) * PR
    : 4 * PR;

  const canvasW = displayWidth * PR + rawPad * 2;
  const canvasH = textH + rawPad * 2;

  const canvas = document.createElement('canvas');
  canvas.width  = canvasW;
  canvas.height = canvasH;

  const ctx = canvas.getContext('2d')!;
  ctx.font = buildFont(fontStyle, fontSize * PR, fontFamily);
  ctx.textBaseline = 'top';
  if (lsDisplay !== 0 && 'letterSpacing' in ctx) {
    (ctx as any).letterSpacing = `${lsDisplay * PR}px`;
  }

  // ── Dispatch to effect renderer ────────────────────────────────────────────
  switch (effect) {
    case 'extrude_3d':
      renderExtrude(ctx, wrappedLines, lineH, fill, align, displayWidth * PR, rawPad, effectParams);
      break;

    case 'image_through_text':
      renderImageMask(ctx, wrappedLines, lineH, fill, align, displayWidth * PR, rawPad, bgImage, canvasW, canvasH);
      break;

    case 'motion_blur':
      renderMotionBlur(ctx, wrappedLines, lineH, fill, align, displayWidth * PR, rawPad, effectParams);
      break;
  }

  // Convert padding back to display-space
  const padDisplay = rawPad / PR;

  return {
    canvas,
    displayHeight: canvasH / PR,
    padX: padDisplay,
    padY: padDisplay,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function buildFont(fontStyle: string, fontSize: number, fontFamily: string): string {
  const style  = fontStyle.includes('italic') ? 'italic' : 'normal';
  const weight = fontStyle.includes('bold') ? 'bold' : 'normal';
  return `${style} ${weight} ${fontSize}px "${fontFamily}", sans-serif`;
}

export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const result: string[] = [];

  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(' ');
    let line = '';

    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        result.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    result.push(line);
  }

  return result;
}

export function drawLine(
  ctx: CanvasRenderingContext2D,
  line: string,
  y: number,
  align: 'left' | 'center' | 'right',
  areaWidth: number,
  originX: number,
): void {
  const lineW = ctx.measureText(line).width;
  let x = originX;
  if (align === 'center') x = originX + (areaWidth - lineW) / 2;
  if (align === 'right')  x = originX + areaWidth - lineW;
  ctx.fillText(line, x, y);
}

function hexToRgba(hex: string, alpha: number): string {
  // Handle 3-char and 6-char hex; fallback to black
  const safe = hex.startsWith('#') ? hex : '#000000';
  const full = safe.length === 4
    ? `#${safe[1]}${safe[1]}${safe[2]}${safe[2]}${safe[3]}${safe[3]}`
    : safe;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── 3D Extrude ───────────────────────────────────────────────────────────────

function renderExtrude(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  lineH: number,
  fill: string,
  align: 'left' | 'center' | 'right',
  areaWidth: number,
  pad: number,
  params: EffectParams,
): void {
  const layers = Math.min(Math.max(2, Math.round(params.extrudeDepth)), 14);

  // Draw extrusion copies from farthest to nearest (back to front)
  for (let i = layers; i >= 1; i--) {
    // Fade extrude color slightly toward the edge for depth
    const alpha = 0.7 + (0.3 * (layers - i)) / layers;
    ctx.fillStyle = hexToRgba(params.extrudeColor, alpha);

    lines.forEach((line, idx) => {
      drawLine(ctx, line, pad + idx * lineH + i, align, areaWidth, pad + i);
    });
  }

  // Main text on top
  ctx.fillStyle = fill;
  lines.forEach((line, idx) => {
    drawLine(ctx, line, pad + idx * lineH, align, areaWidth, pad);
  });
}

// ─── Image Through Text ───────────────────────────────────────────────────────

function renderImageMask(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  lineH: number,
  fill: string,
  align: 'left' | 'center' | 'right',
  areaWidth: number,
  pad: number,
  bgImage: HTMLImageElement | null,
  canvasW: number,
  canvasH: number,
): void {
  if (!bgImage) {
    // No image — fall back to solid colored text
    ctx.fillStyle = fill;
    lines.forEach((line, idx) => {
      drawLine(ctx, line, pad + idx * lineH, align, areaWidth, pad);
    });
    return;
  }

  // Step 1: Draw bold text as a white stencil
  ctx.fillStyle = '#ffffff';
  lines.forEach((line, idx) => {
    drawLine(ctx, line, pad + idx * lineH, align, areaWidth, pad);
  });

  // Step 2: Draw image clipped to the text shape (destination-in)
  ctx.globalCompositeOperation = 'source-in';

  const scale = Math.max(canvasW / bgImage.naturalWidth, canvasH / bgImage.naturalHeight);
  const dW = bgImage.naturalWidth * scale;
  const dH = bgImage.naturalHeight * scale;
  const dX = (canvasW - dW) / 2;
  const dY = (canvasH - dH) / 2;
  ctx.drawImage(bgImage, dX, dY, dW, dH);

  ctx.globalCompositeOperation = 'source-over';
}

// ─── Motion Blur ──────────────────────────────────────────────────────────────

function renderMotionBlur(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  lineH: number,
  fill: string,
  align: 'left' | 'center' | 'right',
  areaWidth: number,
  pad: number,
  params: EffectParams,
): void {
  const amount = Math.max(4, Math.min(params.blurAmount, 30));
  const steps = 10;

  // Draw ghost copies spreading left and right with decreasing opacity
  for (let i = steps; i >= 1; i--) {
    const offset = (i / steps) * amount;
    const alpha = (1 - i / steps) * 0.35;
    ctx.fillStyle = hexToRgba(fill, alpha);

    lines.forEach((line, idx) => {
      const y = pad + idx * lineH;
      // Right trailing copies
      drawLine(ctx, line, y, align, areaWidth, pad + offset);
      // Left trailing copies
      drawLine(ctx, line, y, align, areaWidth, pad - offset);
    });
  }

  // Sharp foreground text
  ctx.fillStyle = fill;
  lines.forEach((line, idx) => {
    drawLine(ctx, line, pad + idx * lineH, align, areaWidth, pad);
  });
}
